/**
 * libraryCache.js — tiny localStorage TTL cache for the All Libraries view.
 *
 * Feature 43. `/library/all` already seeds its Put.io shelves instantly from the
 * persisted `putio` Pinia store, but the slow Plex `recently-added` fan-out
 * (3 retries + backoff) started cold on every fresh mount / reload, so the page
 * "reloaded all the lists" each visit. This persists the Plex payload so a
 * revisit paints immediately and only refreshes in the background.
 *
 * Conventions mirror `putioLanguage.js` / `thumbnailCache.js`:
 *   - JSON envelope `{ ts, data }` in localStorage.
 *   - TTL checked on read; expired entries are removed.
 *   - Every access is try/catch-wrapped (corrupt JSON + quota / private-mode safe).
 *
 * This module has NO Vue / `@/` imports on purpose: the pure `readEnvelope` /
 * `writeEnvelope` helpers are unit-tested directly under `node --test`, and the
 * deviceKey-scoped wrappers take the key as a parameter (the view passes
 * `authStore.deviceKey`) rather than reaching into Pinia here.
 */

// All keys live under this prefix so they're easy to spot / clear.
const KEY_PREFIX = "putio_library_cache_";

// 10 min. The TTL is a FALLBACK safety net, not a skip condition: the view's
// background refresh runs on every mount/activation regardless of cache age, so
// a stale entry is always corrected on the next visit. The TTL only bounds how
// long a cold-start seed (when the background refresh hasn't landed yet, or
// failed) may show pre-existing data — long enough that back-and-forth
// navigation paints instantly, short enough that a never-refreshed entry can't
// surface a title that's minutes-to-hours out of date.
const DEFAULT_TTL = 10 * 60 * 1000;

/**
 * Read + parse a `{ ts, data }` envelope from a Storage-like object, honoring
 * TTL. Pure — `storage` and `now` are injectable so this is testable without a
 * browser. An expired entry is removed and reported as a miss.
 *
 * @returns {{ hit: boolean, data: any, ageMs?: number }}
 */
export function readEnvelope(storage, key, options = {}) {
  if (!storage || !key) return { hit: false, data: null };
  const now = typeof options.now === "number" ? options.now : Date.now();
  const ttl = typeof options.ttl === "number" ? options.ttl : DEFAULT_TTL;
  try {
    const raw = storage.getItem(key);
    if (!raw) return { hit: false, data: null };
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof parsed.ts !== "number"
    ) {
      return { hit: false, data: null };
    }
    const ageMs = now - parsed.ts;
    if (ageMs > ttl) {
      try {
        storage.removeItem(key);
      } catch {
        /* ignore */
      }
      return { hit: false, data: null };
    }
    return { hit: true, data: parsed.data ?? null, ageMs };
  } catch {
    // Corrupt JSON or a throwing storage (SecurityError in some private modes).
    return { hit: false, data: null };
  }
}

/**
 * Persist `data` under `key` as a timestamped envelope. Pure / injectable.
 * Returns false (never throws) on quota / private-mode / bad-input failures.
 */
export function writeEnvelope(storage, key, data, options = {}) {
  if (!storage || !key) return false;
  const now = typeof options.now === "number" ? options.now : Date.now();
  try {
    storage.setItem(key, JSON.stringify({ ts: now, data }));
    return true;
  } catch {
    return false;
  }
}

// --- deviceKey-scoped Plex recently-added wrappers ---------------------------
//
// Keyed by the auth store's opaque per-device `deviceKey` (Feature 24) so two
// device cookies sharing one browser/invite never cross-leak cached Plex
// titles. The view supplies the deviceKey; an invalid/empty one no-ops, so the
// caller transparently falls back to the network.
//
// CONTRACT: `deviceKey` must match `/^[a-f0-9]{16,64}$/` (the server emits a
// 16-char lowercase-hex HMAC — see server/auth/middleware.js `deriveDeviceKey`).
// Anything else (null, empty, uppercase, spaces, path chars, over-long) makes
// every wrapper a no-op (read→null, write/clear→false/return), which the caller
// treats as a cache miss.

function lsOrNull() {
  return typeof localStorage !== "undefined" ? localStorage : null;
}

function sanitizeDeviceKey(deviceKey) {
  if (deviceKey == null) return null;
  const s = String(deviceKey);
  // Match the server's deviceKey format exactly (lowercase hex, ≥16 chars) so a
  // key can never inject a weird/shared storage key. Rejects empty, whitespace,
  // uppercase, `_`/`-`, path separators, and over-long values.
  if (!/^[a-f0-9]{16,64}$/.test(s)) return null;
  return s;
}

function plexKey(deviceKey) {
  const dk = sanitizeDeviceKey(deviceKey);
  return dk ? `${KEY_PREFIX}plex_recent_${dk}` : null;
}

/**
 * Read the cached Plex `recently-added` array for a device. Returns the array
 * on a fresh hit, or `null` (cache miss / expired / no deviceKey / bad cache).
 */
export function readPlexRecentlyAdded(deviceKey, options = {}) {
  const key = plexKey(deviceKey);
  if (!key) return null;
  const { hit, data } = readEnvelope(lsOrNull(), key, {
    ttl: typeof options.ttl === "number" ? options.ttl : DEFAULT_TTL,
  });
  return hit && Array.isArray(data) ? data : null;
}

/**
 * Persist the Plex `recently-added` array for a device. No-ops (returns false)
 * for a bad deviceKey or a non-array payload.
 */
export function writePlexRecentlyAdded(deviceKey, items) {
  const key = plexKey(deviceKey);
  if (!key || !Array.isArray(items)) return false;
  return writeEnvelope(lsOrNull(), key, items);
}

/** Remove a device's cached Plex bucket (e.g. on logout). Safe no-op otherwise. */
export function clearPlexRecentlyAdded(deviceKey) {
  const key = plexKey(deviceKey);
  const ls = lsOrNull();
  if (!key || !ls) return;
  try {
    ls.removeItem(key);
  } catch {
    /* ignore */
  }
}

// --- deviceKey-scoped Continue Watching card seed ----------------------------
//
// The Continue Watching rail's resolved card list (title/poster/target/progress
// per card). Persisting it lets a fresh page load paint the last-known rail
// instantly instead of holding skeletons through the hydrate + per-card
// resolution pass; the rail always revalidates in the background and swaps in
// the fresh list when it lands. Same deviceKey contract as the Plex wrappers.

// 24h: the seed is only ever a stale-while-revalidate first paint — every mount
// refreshes it — so the TTL just bounds how old a cold seed may look before we
// prefer skeletons over day-old cards.
const CW_TTL = 24 * 60 * 60 * 1000;

function continueWatchingKey(deviceKey) {
  const dk = sanitizeDeviceKey(deviceKey);
  return dk ? `${KEY_PREFIX}cw_cards_${dk}` : null;
}

/**
 * Read the cached Continue Watching card array for a device. Returns the array
 * on a fresh hit, or `null` (miss / expired / no deviceKey / bad cache).
 */
export function readContinueWatchingCards(deviceKey) {
  const key = continueWatchingKey(deviceKey);
  if (!key) return null;
  const { hit, data } = readEnvelope(lsOrNull(), key, { ttl: CW_TTL });
  return hit && Array.isArray(data) ? data : null;
}

/**
 * Persist the resolved Continue Watching card array for a device. No-ops
 * (returns false) for a bad deviceKey or a non-array payload.
 */
export function writeContinueWatchingCards(deviceKey, cards) {
  const key = continueWatchingKey(deviceKey);
  if (!key || !Array.isArray(cards)) return false;
  return writeEnvelope(lsOrNull(), key, cards);
}

/** Remove a device's cached Continue Watching seed (e.g. on logout). */
export function clearContinueWatchingCards(deviceKey) {
  const key = continueWatchingKey(deviceKey);
  const ls = lsOrNull();
  if (!key || !ls) return;
  try {
    ls.removeItem(key);
  } catch {
    /* ignore */
  }
}
